import React, { useEffect, useMemo, useRef, useState } from 'react';
import useIsMobile from '../hooks/useIsMobile';

const emptyForm = {
  title: '', first_name: '', last_name: '', nationality: '',
  id_proof_type: '', id_proof_no: '', gst_number: '', company_name: '',
  mobile_number: '', email: ''
};

const STATUS_COLORS = {
  CHECKED_IN:   { bg: '#d1fae5', color: '#065f46' },
  CHECKED_OUT:  { bg: 'var(--theme-border-light)', color: '#475569' },
  CONFIRMED:    { bg: '#dbeafe', color: '#1e40af' },
  TENTATIVE:    { bg: '#fef9c3', color: '#854d0e' },
  CANCELLED:    { bg: '#fee2e2', color: '#991b1b' },
  NO_SHOW:      { bg: '#f3e8ff', color: '#6b21a8' },
};

const FILTER_TABS = [
  { key: 'ALL', label: 'All Guests' },
  { key: 'IN_HOUSE', label: 'In-House' },
  { key: 'CHECKED_OUT', label: 'Checked Out' },
  { key: 'REPEAT', label: 'Repeat Guests' },
];

const SORT_OPTIONS = [
  { key: 'recent', label: 'Recent First' },
  { key: 'name', label: 'Name A-Z' },
  { key: 'stays', label: 'Most Stays' },
  { key: 'revenue', label: 'Highest Revenue' },
  { key: 'last_visit', label: 'Last Visit' },
];

function fmtDate(val) {
  if (!val) return '—';
  try {
    return new Date(val).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return val; }
}

function fmtMoney(val) {
  const n = Number(val || 0);
  if (!n) return '—';
  return n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function StatusBadge({ status }) {
  const s = STATUS_COLORS[status] || { bg: '#f3f4f6', color: 'var(--theme-text)' };
  return (
    <span style={{ background: s.bg, color: s.color, fontSize: '0.72rem', fontWeight: 600,
      padding: '0.15rem 0.55rem', borderRadius: '999px', whiteSpace: 'nowrap' }}>
      {status?.replace('_', ' ') || '—'}
    </span>
  );
}

function PmsGuestMaster({ token, selectedProperty, onReCheckIn, onMakeReservation }) {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [filterTab, setFilterTab] = useState('ALL');
  const [sortBy, setSortBy] = useState('recent');

  // Add/Edit form
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  // Profile view
  const [activeGuestId, setActiveGuestId] = useState(null);
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);

  // Scan image modal
  const [scanModal, setScanModal] = useState(null);

  const searchTimer = useRef(null);

  const hdrs = () => ({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || ''
  });

  const loadRows = async (q = search) => {
    if (!selectedProperty?.id) { setRows([]); return; }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`/api/v1/pms/guests?all=1&search=${encodeURIComponent(q)}`, { headers: hdrs() });
      const data = await res.json().catch(() => []);
      if (!res.ok) throw new Error(data?.error || 'Failed to load guests');
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.message || 'Failed to load guests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setError(''); setMessage(''); setEditingId(''); setForm(emptyForm);
    setShowForm(false); setActiveGuestId(null); setProfile(null);
    setSearch(''); setSearchInput(''); setFilterTab('ALL'); setSortBy('recent');
    loadRows('');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedProperty?.id]);

  const handleSearchChange = (val) => {
    setSearchInput(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setSearch(val);
      loadRows(val);
    }, 350);
  };

  // Filter + Sort
  const filteredRows = useMemo(() => {
    let filtered = rows;
    if (filterTab === 'IN_HOUSE') {
      filtered = rows.filter(r => r.last_status === 'CHECKED_IN');
    } else if (filterTab === 'CHECKED_OUT') {
      filtered = rows.filter(r => r.last_status === 'CHECKED_OUT');
    } else if (filterTab === 'REPEAT') {
      filtered = rows.filter(r => Number(r.total_stays) >= 2);
    }

    const sorted = [...filtered];
    if (sortBy === 'name') {
      sorted.sort((a, b) => {
        const na = `${a.first_name || ''} ${a.last_name || ''}`.trim().toLowerCase();
        const nb = `${b.first_name || ''} ${b.last_name || ''}`.trim().toLowerCase();
        return na.localeCompare(nb);
      });
    } else if (sortBy === 'stays') {
      sorted.sort((a, b) => Number(b.total_stays || 0) - Number(a.total_stays || 0));
    } else if (sortBy === 'revenue') {
      sorted.sort((a, b) => Number(b.total_revenue || 0) - Number(a.total_revenue || 0));
    } else if (sortBy === 'last_visit') {
      sorted.sort((a, b) => {
        const da = a.last_visit || '';
        const db = b.last_visit || '';
        return db.localeCompare(da);
      });
    }
    // 'recent' = default order from backend (created_at DESC)
    return sorted;
  }, [rows, filterTab, sortBy]);

  // Tab counts
  const tabCounts = useMemo(() => ({
    ALL: rows.length,
    IN_HOUSE: rows.filter(r => r.last_status === 'CHECKED_IN').length,
    CHECKED_OUT: rows.filter(r => r.last_status === 'CHECKED_OUT').length,
    REPEAT: rows.filter(r => Number(r.total_stays) >= 2).length,
  }), [rows]);

  const loadProfile = async (guestId) => {
    if (activeGuestId === guestId) {
      setActiveGuestId(null); setProfile(null); return;
    }
    setActiveGuestId(guestId);
    setProfile(null);
    setProfileLoading(true);
    try {
      const res = await fetch(`/api/v1/pms/guests/${guestId}`, { headers: hdrs() });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to load profile');
      setProfile(data);
    } catch (err) {
      setError(err?.message || 'Failed to load guest profile');
      setActiveGuestId(null);
    } finally {
      setProfileLoading(false);
    }
  };

  const onSave = async () => {
    if (!selectedProperty?.id) return setError('Select property first');
    if (!String(form.first_name || '').trim()) return setError('First Name is required');
    setError(''); setMessage('');
    setSaving(true);
    try {
      const url = editingId ? `/api/v1/pms/guests/${editingId}` : '/api/v1/pms/guests';
      const method = editingId ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers: hdrs(), body: JSON.stringify({
        title: form.title.trim(), first_name: form.first_name.trim(),
        last_name: form.last_name.trim(), nationality: form.nationality.trim(),
        id_proof_type: form.id_proof_type.trim(), id_proof_no: form.id_proof_no.trim(),
        gst_number: form.gst_number.trim(), company_name: form.company_name.trim(),
        mobile_number: form.mobile_number.trim(), email: form.email.trim()
      })});
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to save guest');
      setMessage(`Guest ${editingId ? 'updated' : 'added'} successfully.`);
      setEditingId(''); setForm(emptyForm); setShowForm(false);
      loadRows(search);
    } catch (err) {
      setError(err?.message || 'Failed to save guest');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (row) => {
    setEditingId(String(row.guest_id));
    setForm({
      title: row.title || '', first_name: row.first_name || '',
      last_name: row.last_name || '', nationality: row.nationality || '',
      id_proof_type: row.id_proof_type || '', id_proof_no: row.id_proof_no || '',
      gst_number: row.gst_number || '', company_name: row.company_name || '',
      mobile_number: row.mobile_number || '', email: row.email || ''
    });
    setShowForm(true);
    setActiveGuestId(null); setProfile(null);
    setError(''); setMessage('');
  };

  const cancelForm = () => {
    setEditingId(''); setForm(emptyForm); setShowForm(false); setError('');
  };

  const formatName = (row) =>
    [row.title, row.first_name, row.last_name].filter(v => String(v || '').trim()).join(' ') || '—';

  const thS = { padding: '0.55rem 0.75rem', textAlign: 'left', fontWeight: 600,
    fontSize: '0.75rem', color: 'var(--theme-text)', background: 'var(--theme-bg-light)',
    borderBottom: '2px solid #e5e7eb', whiteSpace: 'nowrap' };
  const tdS = { padding: '0.5rem 0.75rem', borderBottom: '1px solid var(--theme-border-light)',
    verticalAlign: 'middle', fontSize: '0.85rem' };
  const inpS = { padding: '0.45rem 0.6rem', borderRadius: 5,
    border: '1px solid #d1d5db', fontSize: '0.875rem', width: '100%' };
  const labelS = { fontSize: '0.75rem', fontWeight: 600, color: 'var(--theme-text)',
    marginBottom: '0.2rem', display: 'block' };

  const tabStyle = (active) => ({
    padding: '0.4rem 0.9rem', borderRadius: 6, fontSize: '0.82rem', fontWeight: 600,
    border: active ? '2px solid var(--theme-primary-dark)' : '1px solid #d1d5db',
    background: active ? '#ebf0ff' : '#fff',
    color: active ? 'var(--theme-primary-dark)' : 'var(--theme-text-secondary)',
    cursor: active ? 'default' : 'pointer',
  });

  return (
    <div className="page-shell">
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.75rem' }}>
        <div>
          <div style={{ fontSize: '0.78rem', color: '#6b7280', marginBottom: '0.2rem' }}>
            <span>Front Office</span>{' '}
            <span style={{ color: '#cbd5e0' }}>&rsaquo;</span>{' '}
            <span style={{ color: 'var(--theme-primary-dark)', fontWeight: 600 }}>Guest Master</span>
          </div>
          <h2 style={{ margin: 0, color: 'var(--theme-primary-dark)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            Guest Master
            {!loading && (
              <span style={{ fontSize: '0.8rem', fontWeight: 500, color: 'var(--theme-text-secondary)',
                background: 'var(--theme-border-light)', padding: '0.15rem 0.6rem', borderRadius: 999 }}>
                {rows.length} guest{rows.length !== 1 ? 's' : ''}
              </span>
            )}
          </h2>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            placeholder="Search name, mobile, ID..."
            value={searchInput}
            onChange={e => handleSearchChange(e.target.value)}
            style={{ ...inpS, width: 240 }}
          />
          <button
            className="btn-primary"
            style={{ padding: '0.45rem 1.1rem', fontSize: '0.875rem' }}
            onClick={() => { setShowForm(!showForm); setEditingId(''); setForm(emptyForm); setError(''); }}
          >
            {showForm && !editingId ? 'Cancel' : '+ Add Guest'}
          </button>
        </div>
      </div>

      {/* Filter Tabs + Sort */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.75rem' }}>
        <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
          {FILTER_TABS.map(tab => (
            <button key={tab.key} style={tabStyle(filterTab === tab.key)}
              onClick={() => setFilterTab(tab.key)}>
              {tab.label}
              <span style={{ fontSize: '0.72rem', marginLeft: 4, color: filterTab === tab.key ? 'var(--theme-primary-dark)' : 'var(--theme-text-muted)' }}>
                ({tabCounts[tab.key] || 0})
              </span>
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
          <span style={{ fontSize: '0.78rem', color: 'var(--theme-text-secondary)', fontWeight: 500 }}>Sort:</span>
          <select value={sortBy} onChange={e => setSortBy(e.target.value)}
            style={{ padding: '0.3rem 0.5rem', borderRadius: 5, border: '1px solid #d1d5db', fontSize: '0.82rem' }}>
            {SORT_OPTIONS.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
          </select>
        </div>
      </div>

      {error && <div className="error-banner" style={{ marginBottom: '0.75rem' }}>{error}</div>}
      {message && (
        <div style={{ background: '#f0fdf4', border: '1px solid #86efac', color: '#166534',
          padding: '0.6rem 1rem', borderRadius: 6, marginBottom: '0.75rem', fontSize: '0.875rem' }}>
          {message}
        </div>
      )}

      {/* Add / Edit Form */}
      {showForm && (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8,
          padding: '1.25rem', marginBottom: '1.25rem' }}>
          <h4 style={{ margin: '0 0 1rem', color: '#1e3a5f' }}>
            {editingId ? 'Edit Guest' : 'Add New Guest'}
          </h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(170px,1fr))', gap: '0.75rem' }}>
            {[
              { key: 'title', label: 'Title', isSelect: true,
                opts: ['', 'Mr', 'Ms', 'Mrs', 'Dr', 'Mx'] },
              { key: 'first_name', label: 'First Name *' },
              { key: 'last_name', label: 'Last Name' },
              { key: 'mobile_number', label: 'Mobile' },
              { key: 'email', label: 'Email' },
              { key: 'nationality', label: 'Nationality' },
              { key: 'id_proof_type', label: 'ID Proof Type', isSelect: true,
                opts: ['', 'Aadhaar', 'PAN Card', 'Passport', 'Driving License', 'Voter ID', 'Other'] },
              { key: 'id_proof_no', label: 'ID Proof No', isIdNo: true },
              { key: 'company_name', label: 'Company' },
              { key: 'gst_number', label: 'GST No' },
            ].map(f => (
              <div key={f.key}>
                <label style={labelS}>{f.label}</label>
                {f.isSelect ? (
                  <select style={inpS} value={form[f.key]}
                    onChange={e => setForm({ ...form, [f.key]: e.target.value })}>
                    {f.opts.map(o => <option key={o} value={o}>{o || '— Select —'}</option>)}
                  </select>
                ) : f.isIdNo ? (
                  <input style={inpS} value={form[f.key]}
                    onChange={e => setForm({ ...form, [f.key]: e.target.value.toUpperCase().replace(/[^A-Z0-9\-]/g, '') })} />
                ) : (
                  <input style={inpS} value={form[f.key]}
                    onChange={e => setForm({ ...form, [f.key]: e.target.value })} />
                )}
              </div>
            ))}
          </div>
          <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem' }}>
            <button className="btn-primary" style={{ padding: '0.5rem 1.4rem' }}
              onClick={onSave} disabled={saving}>
              {saving ? 'Saving...' : editingId ? 'Update Guest' : 'Save Guest'}
            </button>
            <button style={{ padding: '0.5rem 1rem', borderRadius: 5, border: '1px solid #d1d5db',
              background: '#fff', cursor: 'pointer', fontSize: '0.875rem', color: 'var(--theme-text)' }}
              onClick={cancelForm}>Cancel</button>
          </div>
        </div>
      )}

      {/* Guest List */}
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>Loading guests...</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr>
                  <th style={thS}>Guest Name</th>
                  <th style={thS}>Mobile</th>
                  <th style={thS}>ID Proof</th>
                  <th style={{ ...thS, textAlign: 'center' }}>Stays</th>
                  <th style={thS}>Last Visit</th>
                  <th style={thS}>Last Room</th>
                  <th style={{ ...thS, textAlign: 'right' }}>Revenue</th>
                  <th style={thS}>Status</th>
                  <th style={thS}>Scans</th>
                  <th style={{ ...thS, textAlign: 'center' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.length === 0 && (
                  <tr>
                    <td colSpan={10} style={{ padding: '2rem', textAlign: 'center', color: '#9ca3af' }}>
                      {search ? `No guests found for "${search}"` : filterTab !== 'ALL' ? 'No guests in this category.' : 'No guests registered yet.'}
                    </td>
                  </tr>
                )}
                {filteredRows.map(row => {
                  const isOpen = activeGuestId === row.guest_id;
                  const isRepeat = Number(row.total_stays) >= 2;
                  return (
                    <React.Fragment key={row.guest_id}>
                      <tr style={{ background: isOpen ? '#f0f7ff' : 'transparent',
                        transition: 'background 0.15s' }}>
                        <td style={tdS}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                            <div style={{ fontWeight: 600 }}>{formatName(row)}</div>
                            {isRepeat && (
                              <span title="Repeat Guest" style={{ fontSize: '0.65rem', fontWeight: 700,
                                background: '#fef3c7', color: '#92400e', padding: '0.1rem 0.35rem',
                                borderRadius: 3, whiteSpace: 'nowrap' }}>REPEAT</span>
                            )}
                            {row.is_foreign_guest && (
                              <span title="Foreign Guest" style={{ fontSize: '0.65rem', fontWeight: 700,
                                background: '#e0e7ff', color: '#3730a3', padding: '0.1rem 0.35rem',
                                borderRadius: 3, whiteSpace: 'nowrap' }}>FRRO</span>
                            )}
                          </div>
                          {row.email && <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>{row.email}</div>}
                        </td>
                        <td style={tdS}>{row.mobile_number || '—'}</td>
                        <td style={tdS}>
                          {row.id_proof_type || row.id_proof_no
                            ? <span>{row.id_proof_type}{row.id_proof_type && row.id_proof_no ? ' — ' : ''}{row.id_proof_no}</span>
                            : <span style={{ color: '#9ca3af' }}>—</span>}
                        </td>
                        <td style={{ ...tdS, textAlign: 'center' }}>
                          {Number(row.total_reservations) > 0 ? (
                            <span title={`${row.total_stays} stay(s) / ${row.total_reservations} reservation(s)`}>
                              <span style={{ fontWeight: 700, color: '#1e3a5f' }}>{row.total_stays}</span>
                              <span style={{ color: '#9ca3af', fontSize: '0.75rem' }}> / {row.total_reservations}</span>
                            </span>
                          ) : <span style={{ color: '#9ca3af' }}>—</span>}
                        </td>
                        <td style={tdS}>{row.last_visit ? fmtDate(row.last_visit) : <span style={{ color: '#9ca3af' }}>—</span>}</td>
                        <td style={tdS}>
                          {row.last_room
                            ? <span style={{ fontFamily: 'monospace', fontSize: '0.82rem', fontWeight: 600, color: '#1e3a5f' }}>{row.last_room}</span>
                            : <span style={{ color: '#9ca3af' }}>—</span>}
                        </td>
                        <td style={{ ...tdS, textAlign: 'right' }}>
                          {Number(row.total_revenue) > 0
                            ? <span style={{ fontWeight: 600, color: '#166534' }}>{fmtMoney(row.total_revenue)}</span>
                            : <span style={{ color: '#9ca3af' }}>—</span>}
                        </td>
                        <td style={tdS}>{row.last_status ? <StatusBadge status={row.last_status} /> : <span style={{ color: '#9ca3af' }}>—</span>}</td>
                        <td style={{ ...tdS, textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: '0.3rem', justifyContent: 'center' }}>
                            {row.has_id_scan && (
                              <span title="ID Document Scan" onClick={() => loadProfile(row.guest_id)}
                                style={{ display: 'inline-flex', alignItems: 'center', padding: '2px 7px', borderRadius: 4, background: '#eef2ff', color: '#4338ca', fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer', border: '1px solid #c7d2fe', letterSpacing: '0.02em' }}>
                                ID
                              </span>
                            )}
                            {row.has_passport_scan && (
                              <span title="Passport Scan" onClick={() => loadProfile(row.guest_id)}
                                style={{ display: 'inline-flex', alignItems: 'center', padding: '2px 7px', borderRadius: 4, background: '#f0fdf4', color: '#166534', fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer', border: '1px solid #bbf7d0', letterSpacing: '0.02em' }}>
                                PP
                              </span>
                            )}
                            {!row.has_id_scan && !row.has_passport_scan && (
                              <span style={{ color: '#d1d5db', fontSize: '0.78rem' }}>—</span>
                            )}
                          </div>
                        </td>
                        <td style={{ ...tdS, textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: '0.4rem', justifyContent: 'center' }}>
                            <button
                              style={{ fontSize: '0.78rem', padding: '0.28rem 0.75rem', borderRadius: 4,
                                cursor: 'pointer', border: '1px solid #6366f1',
                                background: isOpen ? '#6366f1' : '#eef2ff',
                                color: isOpen ? '#fff' : '#4338ca', fontWeight: 600 }}
                              onClick={() => loadProfile(row.guest_id)}
                            >
                              {isOpen ? 'Close' : 'View'}
                            </button>
                            <button
                              style={{ fontSize: '0.78rem', padding: '0.28rem 0.75rem', borderRadius: 4,
                                cursor: 'pointer', border: '1px solid #d1d5db', background: '#fff', color: 'var(--theme-text)' }}
                              onClick={() => startEdit(row)}
                            >Edit</button>
                          </div>
                        </td>
                      </tr>

                      {/* Inline Profile Panel */}
                      {isOpen && (
                        <tr>
                          <td colSpan={10} style={{ padding: 0, background: '#f8fbff',
                            borderBottom: '2px solid #c7d2fe' }}>
                            {profileLoading ? (
                              <div style={{ padding: '1.5rem', textAlign: 'center', color: '#6b7280' }}>
                                Loading profile...
                              </div>
                            ) : profile ? (
                              <GuestProfilePanel
                                guest={profile.guest}
                                stays={profile.stays}
                                onViewScan={(src, name) => setScanModal({ src, name })}
                                onReCheckIn={onReCheckIn}
                                onMakeReservation={onMakeReservation}
                              />
                            ) : null}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Scan Image Modal */}
      {scanModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
          zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setScanModal(null)}>
          <div style={{ background: '#fff', borderRadius: 10, padding: '1rem',
            maxWidth: '90vw', maxHeight: '90vh', overflow: 'auto' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              marginBottom: '0.75rem' }}>
              <span style={{ fontWeight: 600, color: '#1e3a5f' }}>{scanModal.name}</span>
              <button style={{ border: 'none', background: 'none', fontSize: '1.3rem',
                cursor: 'pointer', color: 'var(--theme-text)' }} onClick={() => setScanModal(null)}>&times;</button>
            </div>
            {(Array.isArray(scanModal.src) ? scanModal.src : [scanModal.src]).map((src, idx) => (
              <img key={idx} src={src} alt={scanModal.name}
                style={{ maxWidth: '80vw', maxHeight: '75vh', borderRadius: 6, display: 'block',
                  marginBottom: idx < (Array.isArray(scanModal.src) ? scanModal.src.length : 1) - 1 ? '0.75rem' : 0 }} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ---- Guest Profile Panel ----
function GuestProfilePanel({ guest: g, stays, onViewScan, onReCheckIn, onMakeReservation }) {
  const { isMobile } = useIsMobile();
  const fieldS = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 1.5rem' };
  const lS = { fontSize: '0.72rem', color: '#9ca3af', fontWeight: 500,
    textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.1rem' };
  const vS = { fontSize: '0.875rem', color: '#1f2937', fontWeight: 500, marginBottom: '0.85rem' };

  const field = (label, value) => (
    <div>
      <div style={lS}>{label}</div>
      <div style={vS}>{value || <span style={{ color: '#9ca3af' }}>—</span>}</div>
    </div>
  );

  const thS = { padding: '0.45rem 0.75rem', fontWeight: 600, fontSize: '0.75rem',
    color: 'var(--theme-text)', background: 'var(--theme-border-light)', textAlign: 'left', whiteSpace: 'nowrap' };
  const tdS = { padding: '0.45rem 0.75rem', fontSize: '0.82rem',
    borderBottom: '1px solid var(--theme-border-light)', verticalAlign: 'middle' };

  return (
    <div style={{ padding: isMobile ? '0.75rem' : '1.25rem 1.5rem', display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'minmax(360px,1fr) minmax(360px,1.2fr)', gap: '1.5rem', flexWrap: 'wrap' }}>

      {/* LEFT — Guest Profile */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '1rem' }}>
          <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'var(--theme-primary-dark)',
            color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 700, fontSize: '1.1rem', flexShrink: 0 }}>
            {(g.first_name || '?')[0].toUpperCase()}
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: '1rem', color: '#1e3a5f' }}>
              {[g.title, g.first_name, g.last_name].filter(Boolean).join(' ')}
            </div>
            <div style={{ fontSize: '0.78rem', color: '#6b7280' }}>Guest ID #{g.guest_id}</div>
          </div>
          {g.is_foreign_guest && (
            <span style={{ background: '#fef3c7', color: '#92400e', fontSize: '0.72rem',
              fontWeight: 700, padding: '0.2rem 0.6rem', borderRadius: 999, marginLeft: 'auto' }}>
              FRRO / Foreign
            </span>
          )}
        </div>

        {/* Quick Actions */}
        {(typeof onReCheckIn === 'function' || typeof onMakeReservation === 'function') && (
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.1rem', flexWrap: 'wrap' }}>
            {typeof onReCheckIn === 'function' && (
              <button
                onClick={() => onReCheckIn(g)}
                style={{ display: 'flex', alignItems: 'center', gap: '0.35rem',
                  padding: '0.45rem 1.1rem', borderRadius: 6, border: 'none',
                  background: 'var(--theme-success)', color: '#fff', cursor: 'pointer',
                  fontSize: '0.82rem', fontWeight: 700 }}>
                + Re Check-In
              </button>
            )}
            {typeof onMakeReservation === 'function' && (
              <button
                onClick={() => onMakeReservation(g)}
                style={{ display: 'flex', alignItems: 'center', gap: '0.35rem',
                  padding: '0.45rem 1.1rem', borderRadius: 6, border: 'none',
                  background: '#2563eb', color: '#fff', cursor: 'pointer',
                  fontSize: '0.82rem', fontWeight: 700 }}>
                Make Reservation
              </button>
            )}
          </div>
        )}

        {/* Contact */}
        <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#6366f1',
          textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.6rem' }}>
          Contact
        </div>
        <div style={fieldS}>
          {field('Mobile', g.mobile_number)}
          {field('Email', g.email)}
          {field('Company', g.company_name)}
          {field('GST No', g.gst_number)}
        </div>

        {/* Personal */}
        <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#6366f1',
          textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.6rem' }}>
          Personal
        </div>
        <div style={fieldS}>
          {field('Date of Birth', fmtDate(g.dob))}
          {field('Gender', g.gender)}
          {field('Nationality', g.nationality)}
          {field('Purpose of Visit', g.purpose_of_visit)}
          {field('Address', g.address)}
        </div>

        {/* ID / Compliance */}
        <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#6366f1',
          textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.6rem' }}>
          Identity &amp; Compliance
        </div>
        <div style={fieldS}>
          {field('ID Type', g.id_proof_type)}
          {field('ID Number', g.id_proof_no)}
          {field('Passport No', g.passport_number)}
          {field('Visa No', g.visa_number)}
          {field('Visa Expiry', fmtDate(g.visa_expiry_date))}
          {field('Arrival Stamp Date', fmtDate(g.arrival_stamp_date))}
          {field('Date of Arrival in India', fmtDate(g.date_of_arrival_in_india))}
          {field('Place of Issue', g.place_of_issue)}
        </div>

        {/* Scan Images — id_scan_data is a multi-file slot (array), so an
            empty array is truthy in JS and must be length-checked, unlike
            passport_scan_data which stays a single value/string. */}
        {((Array.isArray(g.id_scan_data) ? g.id_scan_data.length > 0 : g.id_scan_data) || g.passport_scan_data) && (
          <>
            <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#6366f1',
              textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.6rem' }}>
              Document Scans
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              {(Array.isArray(g.id_scan_data) ? g.id_scan_data.length > 0 : g.id_scan_data) && (
                <button
                  onClick={() => onViewScan(g.id_scan_data, g.id_scan_name || 'ID Document')}
                  style={{ display: 'flex', alignItems: 'center', gap: '0.4rem',
                    padding: '0.45rem 1rem', borderRadius: 6, border: '1px solid #6366f1',
                    background: '#eef2ff', color: '#4338ca', cursor: 'pointer',
                    fontSize: '0.82rem', fontWeight: 600 }}>
                  View ID Scan{Array.isArray(g.id_scan_data) && g.id_scan_data.length > 1 ? ` (${g.id_scan_data.length})` : ''}
                </button>
              )}
              {g.passport_scan_data && (
                <button
                  onClick={() => onViewScan(g.passport_scan_data, g.passport_scan_name || 'Passport')}
                  style={{ display: 'flex', alignItems: 'center', gap: '0.4rem',
                    padding: '0.45rem 1rem', borderRadius: 6, border: '1px solid #6366f1',
                    background: '#eef2ff', color: '#4338ca', cursor: 'pointer',
                    fontSize: '0.82rem', fontWeight: 600 }}>
                  View Passport Scan
                </button>
              )}
            </div>
          </>
        )}
      </div>

      {/* RIGHT — Stay History */}
      <div>
        <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#6366f1',
          textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.75rem' }}>
          Stay History ({stays.length} {stays.length === 1 ? 'reservation' : 'reservations'})
        </div>
        {stays.length === 0 ? (
          <div style={{ color: '#9ca3af', fontSize: '0.85rem', padding: '1rem',
            background: 'var(--theme-bg-light)', borderRadius: 6, textAlign: 'center' }}>
            No reservation history found for this guest.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={thS}>Res #</th>
                  <th style={thS}>Status</th>
                  <th style={thS}>Arrival</th>
                  <th style={thS}>Departure</th>
                  <th style={thS}>Room(s)</th>
                  <th style={thS}>Source</th>
                </tr>
              </thead>
              <tbody>
                {stays.map(s => {
                  const sc = STATUS_COLORS[s.status] || { bg: '#f3f4f6', color: 'var(--theme-text)' };
                  return (
                    <tr key={s.reservation_id}>
                      <td style={tdS}>
                        <span style={{ fontFamily: 'monospace', fontSize: '0.8rem',
                          background: '#f3f4f6', padding: '0.1rem 0.35rem', borderRadius: 3 }}>
                          #{s.reservation_id}
                        </span>
                      </td>
                      <td style={tdS}>
                        <span style={{ background: sc.bg, color: sc.color, fontSize: '0.72rem',
                          fontWeight: 600, padding: '0.15rem 0.55rem', borderRadius: 999 }}>
                          {s.status?.replace('_', ' ')}
                        </span>
                      </td>
                      <td style={tdS}>{fmtDate(s.arrival_date)}</td>
                      <td style={tdS}>{fmtDate(s.departure_date)}</td>
                      <td style={tdS}>
                        <span style={{ fontWeight: 500 }}>{s.room_numbers || '—'}</span>
                      </td>
                      <td style={{ ...tdS, fontSize: '0.75rem', color: '#6b7280' }}>
                        {s.booking_source || '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default PmsGuestMaster;
